import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DetailComponents.css';
import airportsData from '../airports.json';

const AddBookingModal = ({ tripId, tripStartDate, tripEndDate, onSave, onClose }) => {
  const [booking, setBooking] = useState({
    type: 'Hotels',
    startDate: new Date(tripStartDate),
    endDate: null,
    bookingName: '',
    details: {}
  });
  const [departureSearch, setDepartureSearch] = useState('');
  const [arrivalSearch, setArrivalSearch] = useState('');
  const [filteredDepartures, setFilteredDepartures] = useState([]);
  const [filteredArrivals, setFilteredArrivals] = useState([]);
  const modalRef = useRef(null);

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleTypeChange = (e) => {
    setBooking({ ...booking, type: e.target.value, details: {} });
    setDepartureSearch('');
    setArrivalSearch('');
  };

  const handleAirportSearch = (search, type) => {
    const lowerSearch = search.toLowerCase();
    const filtered = airportsData.filter(airport => 
      airport.IATA && airport.IATA !== '\\N' && (
        airport.IATA.toLowerCase().includes(lowerSearch) ||
        airport.Name.toLowerCase().includes(lowerSearch) ||
        airport.City.toLowerCase().includes(lowerSearch) ||
        airport.Country.toLowerCase().includes(lowerSearch)
      )
    ).slice(0, 10);
    if (type === 'departure') {
      setDepartureSearch(search);
      setFilteredDepartures(filtered);
    } else {
      setArrivalSearch(search);
      setFilteredArrivals(filtered);
    }
  };

  const handleAirportSelect = (airport, type) => {
    const airportIata = airport.IATA;
    setBooking(prev => ({
      ...prev,
      details: {
        ...prev.details,
        [type === 'departure' ? 'departureAirportIata' : 'arrivalAirportIata']: airportIata
      }
    }));
    if (type === 'departure') {
      setDepartureSearch(airport.IATA);
      setFilteredDepartures([]);
    } else {
      setArrivalSearch(airport.IATA);
      setFilteredArrivals([]);
    }
  };

  const handleSave = () => {
    if (!booking.startDate || !booking.endDate) {
      alert('Please select both Start Date & Time and End Date & Time');
      return;
    }
    if (!booking.bookingName) {
      alert(`Please provide a ${getBookingNameLabel(booking.type)}`);
      return;
    }
    if (booking.endDate < booking.startDate) {
      alert('End Date cannot be before Start Date');
      return;
    }

    const formattedDetails = { ...booking.details };
    if (booking.type === 'Flights' && formattedDetails.luggage) {
      formattedDetails.luggage = formattedDetails.luggage
        .split('\n')
        .map(line => line.trim() ? `• ${line.trim()}` : '')
        .filter(line => line)
        .join('\n');
    }

    if (formattedDetails.notes) {
      formattedDetails.notes = formattedDetails.notes.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    }

    const bookingData = {
      tripId,
      type: booking.type,
      startDate: booking.startDate.toISOString(),
      endDate: booking.endDate.toISOString(),
      bookingName: booking.bookingName,
      details: formattedDetails
    };
    onSave(bookingData);
  };

  const minDate = new Date(tripStartDate);
  const maxDate = new Date(tripEndDate);
  minDate.setDate(minDate.getUTCDate());
  maxDate.setDate(maxDate.getUTCDate());

  // Define booking name labels for each type
  const getBookingNameLabel = (type) => {
    const labels = {
      'Hotels': 'Hotel Name',
      'AirBnb': 'Property Name',
      'Flights': 'Airline',
      'Homes/Villas': 'Property Name',
      'Day Tours & Activities': 'Activity Name',
      'Restaurant': 'Restaurant Name',
      'Car Rental': 'Car Rental Company',
      'Cruise': 'Cruise Line',
      'Transportation': 'Transportation Provider'
    };
    return labels[type] || 'Name';
  };

  // Render type-specific detail fields
  const renderDetailsFields = () => {
    const commonFields = (
      <>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Name on Reservation:</span>
          <input
            className="edit-modal-input"
            value={booking.details.nameOnReservation || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, nameOnReservation: e.target.value } })}
          />
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Confirmation #:</span>
          <input
            className="edit-modal-input"
            value={booking.details.confirmationNumber || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, confirmationNumber: e.target.value } })}
          />
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Notes:</span>
          <textarea
            className="edit-modal-input"
            value={booking.details.notes || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, notes: e.target.value } })}
            rows="4"
            style={{ whiteSpace: 'pre-wrap' }}
            placeholder="Enter notes here, use Enter for line breaks"
          />
        </div>
      </>
    );

    const flightFields = (
      <>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Flight Number:</span>
          <input
            className="edit-modal-input"
            value={booking.details.flightNumber || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, flightNumber: e.target.value } })}
            placeholder="e.g., IB530"
          />
        </div>
        <div className="edit-modal-field" style={{ position: 'relative' }}>
          <span className="edit-modal-label">Departure Airport:</span>
          <input
            className="edit-modal-input"
            value={departureSearch}
            onChange={e => handleAirportSearch(e.target.value, 'departure')}
            placeholder="e.g., LIS, Lisbon, Portugal"
          />
          {filteredDepartures.length > 0 && (
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              border: '1px solid #ccc',
              borderRadius: '3px',
              maxHeight: '150px',
              overflowY: 'auto',
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              zIndex: 1000,
              marginTop: '5px'
            }}>
              {filteredDepartures.map(airport => (
                <li
                  key={airport.id}
                  style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                  onClick={() => handleAirportSelect(airport, 'departure')}
                >
                  {airport.IATA} - {airport.Name} ({airport.City}, {airport.Country})
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="edit-modal-field" style={{ position: 'relative' }}>
          <span className="edit-modal-label">Arrival Airport:</span>
          <input
            className="edit-modal-input"
            value={arrivalSearch}
            onChange={e => handleAirportSearch(e.target.value, 'arrival')}
            placeholder="e.g., MAD, Madrid, Spain"
          />
          {filteredArrivals.length > 0 && (
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              border: '1px solid #ccc',
              borderRadius: '3px',
              maxHeight: '150px',
              overflowY: 'auto',
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              zIndex: 1000,
              marginTop: '5px'
            }}>
              {filteredArrivals.map(airport => (
                <li
                  key={airport.id}
                  style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                  onClick={() => handleAirportSelect(airport, 'arrival')}
                >
                  {airport.IATA} - {airport.Name} ({airport.City}, {airport.Country})
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Luggage:</span>
          <textarea
            className="edit-modal-input"
            value={booking.details.luggage || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, luggage: e.target.value } })}
            rows="3"
          />
        </div>
        {commonFields}
      </>
    );

    const transportFields = (
      <>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Type of Transportation:</span>
          <select
            className="edit-modal-input"
            value={booking.details.transportType || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, transportType: e.target.value } })}
          >
            <option value="">Select Type</option>
            <option value="Taxi">Taxi</option>
            <option value="Limousine">Limousine</option>
            <option value="Shuttle">Shuttle</option>
            <option value="Bus">Bus</option>
            <option value="Van">Van</option>
          </select>
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Pickup Location:</span>
          <input
            className="edit-modal-input"
            value={booking.details.pickupLocation || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, pickupLocation: e.target.value } })}
            placeholder="e.g., Airport Terminal 1"
          />
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Drop-off Location:</span>
          <input
            className="edit-modal-input"
            value={booking.details.dropoffLocation || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, dropoffLocation: e.target.value } })}
            placeholder="e.g., Hotel"
          />
        </div>
        {commonFields}
      </>
    );

    const eventFields = (
      <>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Venue Name:</span>
          <input
            className="edit-modal-input"
            value={booking.details.venueName || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, venueName: e.target.value } })}
            placeholder="e.g., Madison Square Garden"
          />
        </div>
        {commonFields}
      </>
    );

    const dayToursFields = (
      <>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Activity Type:</span>
          <select
            className="edit-modal-input"
            value={booking.details.activityType || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, activityType: e.target.value } })}
          >
            <option value="">Select Type</option>
            <option value="Hike">Hike</option>
            <option value="Adventure">Adventure</option>
            <option value="Tour">Tour</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Location:</span>
          <input
            className="edit-modal-input"
            value={booking.details.location || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, location: e.target.value } })}
            placeholder="e.g., Eiffel Tower, Paris"
          />
        </div>
        {commonFields}
      </>
    );

    const cruiseFields = (
      <>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Ship Name:</span>
          <input
            className="edit-modal-input"
            value={booking.details.shipName || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, shipName: e.target.value } })}
          />
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Departure Port:</span>
          <input
            className="edit-modal-input"
            value={booking.details.departurePort || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, departurePort: e.target.value } })}
          />
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Departure Port Link:</span>
          <input
            className="edit-modal-input"
            value={booking.details.departurePortLink || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, departurePortLink: e.target.value } })}
            placeholder="e.g., https://maps.google.com/?q=lat,lng"
          />
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Arrival Port:</span>
          <input
            className="edit-modal-input"
            value={booking.details.arrivalPort || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, arrivalPort: e.target.value } })}
          />
        </div>
        <div className="edit-modal-field">
          <span className="edit-modal-label">Arrival Port Link:</span>
          <input
            className="edit-modal-input"
            value={booking.details.arrivalPortLink || ''}
            onChange={e => setBooking({ ...booking, details: { ...booking.details, arrivalPortLink: e.target.value } })}
            placeholder="e.g., https://maps.google.com/?q=lat,lng"
          />
        </div>
        {commonFields}
      </>
    );

    switch (booking.type) {
      case 'Hotels':
      case 'Homes/Villas':
      case 'AirBnb':
        return (
          <>
            <div className="edit-modal-field">
              <span className="edit-modal-label">Room Type:</span>
              <input
                className="edit-modal-input"
                value={booking.details.roomType || ''}
                onChange={e => setBooking({ ...booking, details: { ...booking.details, roomType: e.target.value } })}
              />
            </div>
            <div className="edit-modal-field">
              <span className="edit-modal-label">Location:</span>
              <input
                className="edit-modal-input"
                value={booking.details.location || ''}
                onChange={e => setBooking({ ...booking, details: { ...booking.details, location: e.target.value } })}
                placeholder="e.g., https://maps.google.com/?q=lat,lng"
              />
            </div>
            {commonFields}
          </>
        );
      case 'Flights':
        return flightFields;
      case 'Restaurant':
        return (
          <>
            <div className="edit-modal-field">
              <span className="edit-modal-label">Location:</span>
              <input
                className="edit-modal-input"
                value={booking.details.location || ''}
                onChange={e => setBooking({ ...booking, details: { ...booking.details, location: e.target.value } })}
                placeholder="e.g., https://maps.google.com/?q=lat,lng"
              />
            </div>
            <div className="edit-modal-field">
              <span className="edit-modal-label">Party Size:</span>
              <input
                className="edit-modal-input"
                type="number"
                value={booking.details.partySize || ''}
                onChange={e => setBooking({ ...booking, details: { ...booking.details, partySize: e.target.value } })}
                placeholder="e.g., 4"
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            {commonFields}
          </>
        );
      case 'Car Rental':
        return (
          <>
            <div className="edit-modal-field">
              <span className="edit-modal-label">Vehicle Type:</span>
              <input
                className="edit-modal-input"
                value={booking.details.vehicleType || ''}
                onChange={e => setBooking({ ...booking, details: { ...booking.details, vehicleType: e.target.value } })}
                placeholder="e.g., Sedan, SUV"
              />
            </div>
            <div className="edit-modal-field">
              <span className="edit-modal-label">Pickup Location:</span>
              <input
                className="edit-modal-input"
                value={booking.details.pickupLocation || ''}
                onChange={e => setBooking({ ...booking, details: { ...booking.details, pickupLocation: e.target.value } })}
                placeholder="e.g., JFK Airport"
              />
            </div>
            <div className="edit-modal-field">
              <span className="edit-modal-label">Pickup Map Link:</span>
              <input
                className="edit-modal-input"
                value={booking.details.pickupMapLink || ''}
                onChange={e => setBooking({ ...booking, details: { ...booking.details, pickupMapLink: e.target.value } })}
                placeholder="e.g., https://maps.google.com/?q=lat,lng"
              />
            </div>
            <div className="edit-modal-field">
              <span className="edit-modal-label">Drop-off Location:</span>
              <input
                className="edit-modal-input"
                value={booking.details.dropoffLocation || ''}
                onChange={e => setBooking({ ...booking, details: { ...booking.details, dropoffLocation: e.target.value } })}
                placeholder="e.g., MEX Airport"
              />
            </div>
            <div className="edit-modal-field">
              <span className="edit-modal-label">Drop-off Map Link:</span>
              <input
                className="edit-modal-input"
                value={booking.details.dropoffMapLink || ''}
                onChange={e => setBooking({ ...booking, details: { ...booking.details, dropoffMapLink: e.target.value } })}
                placeholder="e.g., https://maps.google.com/?q=lat,lng"
              />
            </div>
            {commonFields}
          </>
        );
      case 'Transportation':
        return transportFields;
      case 'Day Tours & Activities':
        return dayToursFields;
      case 'Event':
        return eventFields;
      case 'Cruise':
        return cruiseFields;
      default:
        return commonFields;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal edit-modal" ref={modalRef}>
        <h3>Add Booking</h3>
        <div className="edit-modal-form">
          <div className="edit-modal-field">
            <span className="edit-modal-label">Type:</span>
            <select className="edit-modal-input" value={booking.type} onChange={handleTypeChange}>
              <option value="Hotels">Hotels</option>
              <option value="AirBnb">AirBnb</option>
              <option value="Homes/Villas">Homes/Villas</option>
              <option value="Flights">Flights</option>
              <option value="Cruise">Cruise</option>
              <option value="Day Tours & Activities">Day Tours & Activities</option>
              <option value="Restaurant">Restaurant</option>
              <option value="Car Rental">Car Rental</option>
              <option value="Transportation">Transportation</option>
              <option value="Event">Event</option>
            </select>
          </div>
          <div className="edit-modal-field">
            <span className="edit-modal-label">Start Date & Time:*</span>
            <DatePicker
              className="edit-modal-input"
              selected={booking.startDate ? new Date(booking.startDate.getTime() + (booking.startDate.getTimezoneOffset() * 60000)) : null}
              onChange={date => {
                if (date) {
                  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes()));
                  setBooking({ ...booking, startDate: utcDate });
                }
              }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MMMM d, yyyy HH:mm"
              minDate={minDate}
              maxDate={maxDate}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>
          <div className="edit-modal-field">
            <span className="edit-modal-label">End Date & Time:*</span>
            <DatePicker
              className="edit-modal-input"
              selected={booking.endDate ? new Date(booking.endDate.getTime() + (booking.endDate.getTimezoneOffset() * 60000)) : null}
              onChange={date => {
                if (date) {
                  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes()));
                  setBooking({ ...booking, endDate: utcDate });
                }
              }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MMMM d, yyyy HH:mm"
              minDate={booking.startDate || minDate}
              maxDate={maxDate}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </div>
          <div className="edit-modal-field">
            <span className="edit-modal-label">{getBookingNameLabel(booking.type)}:*</span>
            <input
              className="edit-modal-input"
              value={booking.bookingName}
              onChange={e => setBooking({ ...booking, bookingName: e.target.value })}
            />
          </div>
          {renderDetailsFields()}
        </div>
        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
          <button className="save-button" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default AddBookingModal;
